import { File, FileStatus } from "@/Features/Files/files.types";
import type { PhotoGridEntry } from "@/Features/Files/components/PhotoGrid";
import type { UploadQueueItem } from "@/Features/Files/Hooks/useUploadQueue";
import { reconcilePendingPhotos } from "@/Features/StoragePhotos";

// A queue item can exist before the server row does (start-upload has not
// returned yet). The grid only reads name, status and link from the file.
const placeholderFile = (upload: UploadQueueItem): File => ({
  file_id: 0,
  uuid: upload.uuid,
  name: upload.file.name,
  size: upload.file.size,
  ext: "",
  description: null,
  created_on: "",
  is_folder: false,
  status: FileStatus.UPLOADING,
  link: "",
  parent: null,
  case: null,
  created_by: null,
});

/**
 * Builds the grid entries from the server rows and the local upload queue,
 * for any photos tab (storage items, evidence items).
 *
 * The queue uuid is the row uuid (start-upload accepts it), so entries merge
 * by uuid: a local in-flight entry wins, a server `uploading` row with no
 * local entry stays a placeholder, and `active` rows render normally. A
 * completed queue item whose row is gone (deleted) renders nothing.
 *
 * Rows keep the order they came in. Each photos API sorts its own list
 * (storage newest first, evidence by name), and each tab's cache update puts
 * a finished upload where a refetch would. Uploads with no server row yet go
 * on top, newest (last selected) first.
 */
export const mergePhotoEntries = (
  serverRows: File[],
  uploads: UploadQueueItem[]
): PhotoGridEntry[] => {
  const merged = reconcilePendingPhotos(serverRows, uploads)
    // A finished upload with no server row was deleted (or is not cached yet);
    // as a queue-only entry it would render a permanent "Uploading..." tile.
    .filter(({ file, upload }) => file || upload?.phase !== "complete");

  const localOnly = merged.filter(({ file }) => !file).reverse();
  const withRow = merged.filter(({ file }) => file);

  return [...localOnly, ...withRow].map(({ file, upload }) => ({
    file: file ?? placeholderFile(upload as UploadQueueItem),
    upload,
  }));
};
